import Cookies from 'js-cookie';
import { NavigationHelper } from './NavigationHelper';
import { getLocationParts } from '@common/utils';

export * from './NavigationHelper';
export { default as i18n } from './i18n';

/** URL fragment key used to hand the login token between the UI's own hosts. */
const AUTH_HANDOFF_KEY = 'authToken';

/**
 * The login token for this tab. The UI runs on the main domain and on every
 * app subdomain, and browser storage is per host, so a token can also arrive
 * in the URL fragment from another UI host (see withAuthHandoff).
 */
export function getAuthToken(): string {
    const storedToken = sessionStorage.getItem('authToken') ?? localStorage.getItem('authToken');
    return getHandoffToken() ?? (storedToken as string);
}

function getHandoffToken(): string | null {
    return new URLSearchParams(window.location.hash.slice(1)).get(AUTH_HANDOFF_KEY);
}

/**
 * Adds the login token to a link to another UI host (the main domain or an app
 * page, never an app's -proxy host). It goes in the URL fragment, which
 * browsers never send to servers, so it stays out of access logs and Referer
 * headers.
 */
export function withAuthHandoff(url: string, authToken?: string | null): string {
    if (!authToken) return url;
    const target = new URL(url, window.location.href);
    target.hash = new URLSearchParams({ [AUTH_HANDOFF_KEY]: authToken }).toString();
    return target.toString();
}

/** Removes a handed-over token from the address bar and history. */
export function clearAuthHandoff() {
    if (getHandoffToken() === null) return;
    window.history.replaceState(window.history.state, document.title, window.location.pathname + window.location.search);
}

/**
 * Older versions kept "remember me" in a cookie on the parent domain. Browsers
 * send that cookie to every app subdomain, and app pages can read it, so
 * delete it wherever the UI loads.
 */
export function clearLegacyAuthCookie() {
    const { maindomain } = getLocationParts();
    Cookies.remove('authToken', { domain: maindomain });
    Cookies.remove('authToken');
}

export function Logout() {
    localStorage.clear();
    sessionStorage.clear();
    clearLegacyAuthCookie();

    window.location.href = NavigationHelper.Home().uri;
    window.location.reload();
}

export function getAppPassword(name: string) {
    return sessionStorage.getItem(`password-${name}`);
}

export function setAppPassword(name: string, password: string) {
    sessionStorage.setItem(`password-${name}`, password);
}

export * from "./CommonNavigationHelper";
export * from "./EventBus";
export * from "./DomainHelper";
export * from "./URLHelper";
